import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Building2, CircleCheckBig, EllipsisVertical, FilterX, Network, RefreshCw, Search, UsersRound } from "lucide-react";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import { useBackofficeIdentity } from "./BackofficeIdentityContext.jsx";
import { useSuperAgentsList, SUPER_AGENTS_DEFAULT_PAGE_SIZE } from "../workerLine/useSuperAgentsList.js";
import SuperAgentRowMenu from "./SuperAgentRowMenu.jsx";
import { getSuperAgent, createSuperAgent, updateSuperAgent, listSuperAgents } from "../workerLine/workerLineApi.js";
import { SuperAgentDetailView, SuperAgentCreateView, SuperAgentEditView, emptySuperAgentForm } from "../workerLine/SuperAgentViews.jsx";
import {
  WORKER_LINE_STATUS_OPTIONS,
  statusBadgeVariant,
  describeListError,
  fieldLabel,
  isJobDemandSide,
  organizationSideLabel,
  organizationSideBadgeVariant,
} from "../workerLine/workerLineShared.js";
import "../workerLine/workerLine.css";
import "../pages/states.css";
// AFTER workerLine.css on purpose: this sheet overrides shared list rules
// (the row button's column layout, the search field's padding), and a CSS
// override that loads first is not an override at all.
import "./superAgentsOverviewPage.css";
import Pagination from "../components/Pagination.jsx";

import PageSkeleton from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "SuperAdmin", "Super Agents"];
const DRAWER_TITLE_ID = "sa-drawer-title";
const SEARCH_DEBOUNCE_MS = 350;

/**
 * P165: the two summary cards at the top of the page.
 *
 * Both counts are READ FROM THE SERVER rather than counted out of the current
 * page's rows: this list is paginated, so counting `items` would quietly report
 * "2 Active" on a page of 25 out of 358. They follow the SEARCH but ignore the
 * status filter on purpose - the cards summarise what was searched for, and the
 * status filter is a view of that summary, so filtering to Suspended must not
 * make a card labelled "Active" change its number.
 *
 * pageSize 1 because only `totalCount` is wanted; the server clamps to [1, 100].
 */
function useSuperAgentCounts(search) {
  const [counts, setCounts] = useState({ total: null, active: null });

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listSuperAgents({ search, page: 1, pageSize: 1 }),
      listSuperAgents({ search, status: "Active", page: 1, pageSize: 1 }),
    ])
      .then(([all, active]) => {
        if (!cancelled) {
          setCounts({ total: all?.totalCount ?? null, active: active?.totalCount ?? null });
        }
      })
      .catch(() => {
        // A summary that cannot be read shows a dash, never a zero - "0 Active"
        // is a claim about the data, and this failure is a claim about the
        // network. The list below reports the real error.
        if (!cancelled) {
          setCounts({ total: null, active: null });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [search]);

  return counts;
}

const SUBTITLE_TEXT = "สายงานระดับบนสุด ดูโครงสร้าง PLC / Agent / แรงงาน และแก้ไขข้อมูลติดต่อได้จากที่นี่";

/**
 * /backoffice/super-agents - Prompt 131/132 Worker Line Admin: Super Agents.
 * Entirely over the real Worker Line Admin API
 * (GET/POST/PATCH /api/backoffice/worker-line/super-agents[/{id}]) - a
 * paginated, scope-isolated list with search/status filter, Create Super
 * Agent, a real detail drawer (info/contacts/PLC summary/counts/next
 * actions), and Edit. Replaces the P085/P086R version of this page, which
 * derived everything from the read-only worker-intake hierarchy tree and
 * left Create disabled - that tree is now only used by the per-Super-Agent
 * Worker Line Org Chart drill-down (SuperAgentLineOrgChartPage, unchanged)
 * and the platform-wide Worker Line Org Chart
 * (../workerLine/WorkerLineOrgChartPage.jsx). This screen never calls a
 * Contact/Safety/e-KYC API (none of those exist anywhere in this codebase).
 */
export default function SuperAgentsOverviewPage() {
  const { data: identity } = useBackofficeIdentity();
  const primaryRole = identity?.primaryRole;
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);

  const [listMenuOpen, setListMenuOpen] = useState(false);
  const listMenuRef = useRef(null);

  const [selectedId, setSelectedId] = useState(null);
  const [drawerView, setDrawerView] = useState(null); // null | "detail" | "create" | "edit"
  const [detailState, setDetailState] = useState({ status: "idle", data: null, error: null, forId: null });
  const [detailReloadToken, setDetailReloadToken] = useState(0);
  // The id whose detail, once it lands, should open straight into the edit form.
  const [editAfterLoad, setEditAfterLoad] = useState(null);

  const [createForm, setCreateForm] = useState(emptySuperAgentForm());
  const [createValidationError, setCreateValidationError] = useState(null);
  const [createSubmit, setCreateSubmit] = useState({ status: "idle", error: null });

  const [editForm, setEditForm] = useState(null);
  const [editValidationError, setEditValidationError] = useState(null);
  const [editSubmit, setEditSubmit] = useState({ status: "idle", error: null });

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const filters = useMemo(
    () => ({ page, pageSize: SUPER_AGENTS_DEFAULT_PAGE_SIZE, search, status: statusFilter }),
    [page, search, statusFilter]
  );

  const { status: listStatus, data: listData, error: listError, refetch: refetchList } = useSuperAgentsList(filters);
  const counts = useSuperAgentCounts(search);

  // The list-level menu closes on any press outside it, like every other menu
  // in this app - a menu that only closes via its own trigger gets left open.
  useEffect(() => {
    if (!listMenuOpen) {
      return undefined;
    }
    const onPointerDown = (event) => {
      if (listMenuRef.current && !listMenuRef.current.contains(event.target)) {
        setListMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [listMenuOpen]);

  useEffect(() => {
    if (!selectedId) {
      setDetailState({ status: "idle", data: null, error: null, forId: null });
      return undefined;
    }

    let cancelled = false;
    setDetailState((previous) => ({
      status: "loading",
      data: previous.forId === selectedId ? previous.data : null,
      error: null,
      forId: selectedId,
    }));

    getSuperAgent(selectedId)
      .then((data) => {
        if (!cancelled) {
          setDetailState({ status: "ready", data, error: null, forId: selectedId });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setDetailState({ status: "error", data: null, error, forId: selectedId });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- detailReloadToken intentionally re-triggers a fetch for the same selectedId
  }, [selectedId, detailReloadToken]);

  function openDetail(id) {
    setSelectedId(id);
    setDrawerView("detail");
    setEditForm(null);
    setEditValidationError(null);
    setEditSubmit({ status: "idle", error: null });
  }

  /**
   * The row menu's "แก้ไขข้อมูล". Editing needs the full record, which only the
   * detail call has, so this opens the drawer and remembers that the edit form
   * is where it should end up - openEdit alone would run against a detail that
   * has not been fetched yet and silently do nothing.
   */
  function openDetailForEdit(id) {
    openDetail(id);
    setEditAfterLoad(id);
  }

  useEffect(() => {
    if (editAfterLoad && detailState.status === "ready" && detailState.forId === editAfterLoad) {
      setEditAfterLoad(null);
      openEdit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openEdit reads the freshly loaded detail; adding it would re-run this on every render
  }, [editAfterLoad, detailState.status, detailState.forId]);

  function openCreate() {
    setCreateForm(emptySuperAgentForm());
    setCreateValidationError(null);
    setCreateSubmit({ status: "idle", error: null });
    setDrawerView("create");
  }

  function openEdit() {
    const detail = detailState.data;
    if (!detail) {
      return;
    }
    setEditForm({
      displayName: detail.displayName ?? "",
      legalName: detail.legalName ?? "",
      ownerName: detail.ownerName ?? "",
      primaryPhoneNumber: detail.primaryPhoneNumber ?? "",
      lineId: detail.lineId ?? "",
      status: detail.status ?? "Active",
      note: detail.note ?? "",
    });
    setEditValidationError(null);
    setEditSubmit({ status: "idle", error: null });
    setDrawerView("edit");
  }

  function closeDrawer() {
    setDrawerView(null);
    setSelectedId(null);
    setEditAfterLoad(null);
    if (searchParams.has("openCreate")) {
      const next = new URLSearchParams(searchParams);
      next.delete("openCreate");
      setSearchParams(next, { replace: true });
    }
  }

  // A CTA from the Worker Line Org Chart's empty state ("ยังไม่มีสายงานในระบบ")
  // links here with ?openCreate=1 so the very first Super Agent can be
  // created without a separate manual step.
  useEffect(() => {
    if (searchParams.get("openCreate") === "1" && drawerView === null) {
      openCreate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount only, opening the drawer must not re-fire on every render
  }, []);

  function handleCreateSubmit() {
    const trimmedName = createForm.displayName.trim();
    if (!trimmedName) {
      setCreateValidationError("กรุณาระบุ displayName");
      return;
    }
    setCreateValidationError(null);
    setCreateSubmit({ status: "submitting", error: null });
    createSuperAgent({ ...createForm, displayName: trimmedName })
      .then(() => {
        setCreateSubmit({ status: "success", error: null });
        refetchList();
        closeDrawer();
      })
      .catch((error) => {
        setCreateSubmit({ status: "error", error });
      });
  }

  function handleEditSubmit() {
    if (!editForm) {
      return;
    }
    const trimmedName = editForm.displayName.trim();
    if (!trimmedName) {
      setEditValidationError("displayName ไม่สามารถเว้นว่างได้");
      return;
    }
    setEditValidationError(null);
    setEditSubmit({ status: "submitting", error: null });
    updateSuperAgent(selectedId, { ...editForm, displayName: trimmedName })
      .then(() => {
        setEditSubmit({ status: "success", error: null });
        setDetailReloadToken((token) => token + 1);
        refetchList();
        setDrawerView("detail");
      })
      .catch((error) => {
        setEditSubmit({ status: "error", error });
      });
  }

  const items = listData?.items ?? [];
  const totalCount = listData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / SUPER_AGENTS_DEFAULT_PAGE_SIZE));
  const hasActiveFilters = Boolean(search || statusFilter);

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-wl-page">
        <header className="wx-wl-head wx-sao-head">
          <div className="wx-sao-head-id">
            {/* The tier's own mark. Every screen below this one (PLC, Agent,
                worker) already leads with an icon for what it lists; this page
                was the odd one out. */}
            <span className="wx-sao-head-icon" aria-hidden="true">
              <UsersRound size={22} />
            </span>
            <div>
              <h1>Super Agents</h1>
              <p>{SUBTITLE_TEXT}</p>
            </div>
          </div>
          {primaryRole && (
            <span className="wx-wl-scope-pill">
              Scope: <b>{primaryRole}</b>
            </span>
          )}
        </header>

        <div className="wx-wl-toolbar">
          <label className="wx-wl-search wx-sao-search">
            <span className="wx-visually-hidden">ค้นหา Super Agent, ชื่อนิติบุคคล, เจ้าของ หรือเบอร์โทร</span>
            <Search className="wx-sao-search-icon" size={15} aria-hidden="true" />
            <input
              type="search"
              placeholder="ค้นหาชื่อสายงาน, เจ้าของ หรือเบอร์โทร..."
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>

          <label className="wx-wl-filter wx-wl-filter--inline">
            <span>สถานะ</span>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="">ทั้งหมด</option>
              {WORKER_LINE_STATUS_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>

          {hasActiveFilters && (
            <button
              type="button"
              className="wx-wl-clear-filters"
              onClick={() => {
                setSearchInput("");
                setSearch("");
                setStatusFilter("");
              }}
            >
              ล้างตัวกรอง
            </button>
          )}

          <div className="wx-wl-toolbar-actions">
            <button type="button" className="wx-button wx-button--primary wx-wl-create-button" onClick={openCreate}>
              + สร้าง Super Agent
            </button>
          </div>
        </div>

        {/* P165: the two numbers this page is opened to check, above the list
            rather than inside it. Both come from the server (see
            useSuperAgentCounts) - counted out of the visible rows they would be
            wrong on any page but the last. */}
        <div className="wx-sao-stats">
          <div className="wx-sao-stat">
            <span className="wx-sao-stat-icon wx-sao-stat-icon--primary" aria-hidden="true">
              <UsersRound size={22} />
            </span>
            <div className="wx-sao-stat-text">
              <span className="wx-sao-stat-label">จำนวน Super Agents ทั้งหมด</span>
              <strong className="wx-sao-stat-value">
                {counts.total === null ? "-" : counts.total.toLocaleString("en-US")}
              </strong>
              <span className="wx-sao-stat-unit">รายการ</span>
            </div>
          </div>

          <div className="wx-sao-stat">
            <span className="wx-sao-stat-icon wx-sao-stat-icon--success" aria-hidden="true">
              <CircleCheckBig size={22} />
            </span>
            <div className="wx-sao-stat-text">
              <span className="wx-sao-stat-label">Active</span>
              <strong className="wx-sao-stat-value">
                {counts.active === null ? "-" : counts.active.toLocaleString("en-US")}
              </strong>
              <span className="wx-sao-stat-unit">รายการ</span>
            </div>
          </div>
        </div>

        <section className="wx-panel wx-wl-list-panel">
          <div className="wx-wl-list-head">
            <h2 className="wx-panel-title">รายชื่อ Super Agent</h2>
            <div className="wx-sao-list-head-aside">
              <span className="wx-wl-list-count">
                {totalCount} รายการ{listStatus === "loading" && listData !== null ? " · กำลังโหลด..." : ""}
              </span>
              {/* Holds only things this screen can really do. There is no export
                  API anywhere in this codebase, so there is no export item -
                  a menu entry that cannot work is worse than a shorter menu. */}
              <div className="wx-wl-rowmenu" ref={listMenuRef}>
                <button
                  type="button"
                  className="wx-wl-rowmenu-trigger"
                  aria-haspopup="menu"
                  aria-expanded={listMenuOpen}
                  aria-label="ตัวเลือกของรายการนี้"
                  onClick={() => setListMenuOpen((open) => !open)}
                >
                  <EllipsisVertical size={16} aria-hidden="true" />
                </button>
                {listMenuOpen && (
                  <div className="wx-wl-rowmenu-list" role="menu">
                    <button
                      type="button"
                      role="menuitem"
                      className="wx-wl-rowmenu-item"
                      onClick={() => {
                        setListMenuOpen(false);
                        refetchList();
                      }}
                    >
                      <RefreshCw size={14} aria-hidden="true" />
                      โหลดรายการใหม่
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="wx-wl-rowmenu-item"
                      disabled={!hasActiveFilters}
                      onClick={() => {
                        setListMenuOpen(false);
                        setSearchInput("");
                        setSearch("");
                        setStatusFilter("");
                      }}
                    >
                      <FilterX size={14} aria-hidden="true" />
                      ล้างตัวกรอง
                    </button>
                    <Link className="wx-wl-rowmenu-item" role="menuitem" to="/backoffice/worker-line/org-chart" onClick={() => setListMenuOpen(false)}>
                      <Network size={14} aria-hidden="true" />
                      เปิดผังสายงานทั้งหมด
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>

          {listStatus === "error" && listData !== null && (
            <p className="wx-wl-inline-error" role="alert">
              {describeListError(listError)}
            </p>
          )}

          {listStatus === "error" && listData === null ? (
            <div className="wx-state-card wx-state-card--danger" role="alert">
              <p className="wx-state-body">{describeListError(listError)}</p>
            </div>
          ) : listStatus === "loading" && listData === null ? (
            <PageSkeleton variant="cards" label="กำลังโหลดรายชื่อ Super Agent..." />
          ) : items.length === 0 ? (
            <p className="wx-empty-list">{hasActiveFilters ? "ไม่พบผลลัพธ์ที่ตรงกับตัวกรองหรือคำค้นหา" : "ยังไม่มี Super Agent ในระบบ"}</p>
          ) : (
            <>
              <div className="wx-wl-table-wrap">
                <table className="wx-wl-table wx-sao-table">
                  <colgroup>
                    <col className="wx-wl-col-name" />
                    <col className="wx-wl-col-owner" />
                    <col className="wx-wl-col-phone" />
                    <col className="wx-wl-col-status" />
                    <col className="wx-sao-col-side" />
                    <col className="wx-wl-col-count" />
                    <col className="wx-wl-col-count" />
                    <col className="wx-wl-col-count" />
                    <col className="wx-sao-col-manage" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Super Agent</th>
                      <th>{fieldLabel("ownerName")}</th>
                      <th>{fieldLabel("primaryPhoneNumber")}</th>
                      <th>{fieldLabel("status")}</th>
                      {/* P156: which side of the business this line is. It
                          changes what every tier below it holds, so it belongs
                          next to the name, not buried in the detail drawer. */}
                      <th>ฝั่ง</th>
                      {/* The three hierarchy counts are the reason this page
                          exists, so they sit together as one numeric group
                          rather than reading like more contact fields. */}
                      <th className="wx-wl-th-num wx-wl-th-group-start">PLC</th>
                      <th className="wx-wl-th-num">Agent</th>
                      <th className="wx-wl-th-num">แรงงาน</th>
                      {/* P165: the row's own actions. It replaces "สร้างเมื่อ",
                          which was the least-read column on the page - a
                          creation date answers a question nobody opens this
                          screen with, and it is still on the record itself. */}
                      <th className="wx-sao-th-manage">จัดการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.superAgentOrganizationId} className="wx-wl-row" onClick={() => openDetail(item.superAgentOrganizationId)}>
                        <td>
                          {/* A real button carries the row's action so the
                              list is keyboard operable - a click handler on
                              <tr> alone is reachable by mouse only. */}
                          <button
                            type="button"
                            className="wx-wl-row-open wx-sao-row-open"
                            onClick={(event) => {
                              event.stopPropagation();
                              openDetail(item.superAgentOrganizationId);
                            }}
                          >
                            {/* Tinted by SIDE, not decoration: on a page where
                                every row looks alike, the colour is the fastest
                                read of which half of the business a line is on,
                                and it says the same thing as the badge further
                                along the row. */}
                            <span
                              className={`wx-sao-row-mark ${isJobDemandSide(item.organizationSide) ? "wx-sao-row-mark--job" : "wx-sao-row-mark--worker"}`}
                              aria-hidden="true"
                            >
                              <Building2 size={17} />
                            </span>
                            <span className="wx-sao-row-id">
                              <strong className="wx-wl-cell-strong">{item.displayName}</strong>
                              <span className="wx-wl-code">{item.superAgentCode}</span>
                            </span>
                          </button>
                        </td>
                        <td>{item.ownerName || "-"}</td>
                        <td className="wx-wl-td-num">{item.primaryPhoneNumber || "-"}</td>
                        <td>
                          <span className={`wx-badge wx-badge--${statusBadgeVariant(item.status)} wx-sao-status`}>
                            <span className="wx-sao-status-dot" aria-hidden="true" />
                            {item.status}
                          </span>
                        </td>
                        <td className="wx-sao-side-cell">
                          <span className={`wx-badge wx-badge--${organizationSideBadgeVariant(item.organizationSide)}`}>
                            {organizationSideLabel(item.organizationSide)}
                          </span>
                        </td>
                        <td className="wx-wl-td-num wx-wl-td-group-start">{item.plcCount}</td>
                        <td className="wx-wl-td-num">{item.agentCount}</td>
                        {/* A job-side line has no workers by design, so a zero
                            here would read as an empty line. Show a dash. */}
                        <td className="wx-wl-td-num wx-wl-td-num--strong">
                          {isJobDemandSide(item.organizationSide) ? "-" : item.workerCount.toLocaleString("en-US")}
                        </td>
                        <td className="wx-sao-td-manage">
                          <SuperAgentRowMenu item={item} onOpenDetail={openDetail} onEdit={openDetailForEdit} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="wx-wl-cards">
                {items.map((item) => (
                  <div className="wx-wl-card" key={item.superAgentOrganizationId} onClick={() => openDetail(item.superAgentOrganizationId)}>
                    <div className="wx-wl-card-top">
                      <div>
                        <strong>{item.displayName}</strong>
                        <span className="wx-wl-card-sub">{item.superAgentCode}</span>
                      </div>
                      <span className={`wx-badge wx-badge--${statusBadgeVariant(item.status)}`}>{item.status}</span>
                    </div>
                    <div className="wx-wl-card-grid">
                      <div>
                        <label>PLC</label>
                        <strong>{item.plcCount}</strong>
                      </div>
                      <div>
                        <label>Agent</label>
                        <strong>{item.agentCount}</strong>
                      </div>
                      <div>
                        <label>แรงงาน</label>
                        <strong>{item.workerCount}</strong>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </section>
      </div>

      <DetailDrawer
        open={drawerView !== null}
        onClose={closeDrawer}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector="tr.wx-wl-row, .wx-wl-card, .wx-wl-create-button"
      >
        {drawerView === "detail" && (
          <SuperAgentDetailView
            titleId={DRAWER_TITLE_ID}
            state={detailState}
            onEdit={openEdit}
            onCreatePlc={(id) => `/backoffice/worker-line/plcs?createForSuperAgentId=${id}`}
            onCreateAgent={(id) => `/backoffice/worker-line/agents?createForSuperAgentId=${id}`}
          />
        )}
        {drawerView === "create" && (
          <SuperAgentCreateView
            titleId={DRAWER_TITLE_ID}
            form={createForm}
            onChange={(patch) => setCreateForm((previous) => ({ ...previous, ...patch }))}
            validationError={createValidationError}
            submitState={createSubmit}
            onSubmit={handleCreateSubmit}
          />
        )}
        {drawerView === "edit" && editForm && (
          <SuperAgentEditView
            titleId={DRAWER_TITLE_ID}
            code={detailState.data?.superAgentCode}
            form={editForm}
            onChange={(patch) => setEditForm((previous) => ({ ...previous, ...patch }))}
            validationError={editValidationError}
            submitState={editSubmit}
            onSubmit={handleEditSubmit}
            onBack={() => setDrawerView("detail")}
          />
        )}
      </DetailDrawer>
    </AppShell>
  );
}
